/* eslint-disable require-await */
import { type Dispatch } from 'redux';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import TagManager from 'react-gtm-module';
import moment from 'moment';
import jwtDecode from 'jwt-decode';
import axios from 'axios';
import { toastConfig } from 'app/utils/constants';
import { type VigilanciaState } from 'app/interfaces/vigilancia/vigilanciaState.interface';
import { type VigilanciaPdfResponse } from 'app/interfaces/vigilancia/vigilanciaPDF.interface';
import { type VigilanciaCertificadosRespose } from 'app/interfaces/vigilancia/vigilanciaCertificados.interface';
import { type AppState } from 'app/App.store';
import { sendTagLog } from '@ui-core/utils/sendTagLog.lib';
import { type AchsJWT } from '@ui-core/interfaces/AchsJWTInterface';
import AxiosClient from '@ui-core/config/axiosClient';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

const token = localStorage.getItem('Authorization');
let azureObjectId: string | undefined;
if (token) {
	const tokenDecoded: AchsJWT = jwtDecode(token);
	azureObjectId = tokenDecoded.azureObjectId;
}

export const initialState: VigilanciaState = {
	loadingPdfVigilancia: false,
	responseDataPdfVigilancia: {} as VigilanciaPdfResponse,
	errorPdfVigilancia: false,
	errorMsgPdfVigilancia: '',
	loadingVigilancia: false,
	responseDataVigilancia: {} as VigilanciaCertificadosRespose,
	errorVigilancia: false,
	errorMsgVigilancia: '',
	pageVigilancia: 1,
	totalVigilancia: 1,
	loadingPdfDownloadVigilancia: false,
};

export const vigilanciaSlice = createSlice({
	name: 'vigilancia',
	initialState,
	reducers: {
		loadingPdfVigilancia: (state) => {
			state.loadingPdfVigilancia = true;
			state.responseDataPdfVigilancia = {} as VigilanciaPdfResponse;
			state.errorPdfVigilancia = false;
			state.errorMsgPdfVigilancia = '';
			state.loadingPdfDownloadVigilancia = false;
		},
		successPdfVigilancia: (
			state,
			action: PayloadAction<VigilanciaPdfResponse>,
		) => {
			state.loadingPdfVigilancia = false;
			state.responseDataPdfVigilancia = action.payload;
			state.errorPdfVigilancia = false;
			state.errorMsgPdfVigilancia = '';
			state.loadingPdfDownloadVigilancia = false;
		},
		errorPdfVigilancia: (state, action: PayloadAction<string>) => {
			state.loadingPdfVigilancia = false;
			state.responseDataPdfVigilancia = {} as VigilanciaPdfResponse;
			state.errorPdfVigilancia = true;
			state.errorMsgPdfVigilancia = action.payload;
			state.loadingPdfDownloadVigilancia = false;
		},
		loadingVigilancia: (state) => {
			state.loadingVigilancia = false;
			state.responseDataVigilancia = {} as VigilanciaCertificadosRespose;
			state.errorVigilancia = false;
			state.errorMsgVigilancia = '';
		},
		successVigilancia: (
			state,
			action: PayloadAction<VigilanciaCertificadosRespose>,
		) => {
			state.loadingVigilancia = true;
			state.responseDataVigilancia = action.payload;
			state.errorVigilancia = false;
			state.errorMsgVigilancia = '';
		},
		errorVigilancia: (state, action: PayloadAction<string>) => {
			state.loadingVigilancia = false;
			state.responseDataVigilancia = {} as VigilanciaCertificadosRespose;
			state.errorVigilancia = true;
			state.errorMsgVigilancia = action.payload;
		},
		setPageVigilancia: (state, action: PayloadAction<number>) => {
			state.pageVigilancia = action.payload;
		},
		setTotalVigilancia: (state, action: PayloadAction<number>) => {
			state.totalVigilancia = action.payload;
		},
		loadingPdfDownloadVigilancia: (state) => {
			state.loadingPdfDownloadVigilancia = true;
		},
		successPdfDownloadVigilancia: (state) => {
			state.loadingPdfDownloadVigilancia = false;
		},
	},
});

const callTagManagerVigilancia = (gaResponse: string) => {
	const dataLayer = {
		event: 'gaEvent',
		gaCategory: 'Trabajador',
		gaAction: 'Resultado exámenes de salud',
		gaLabel: 'Clic Btn: Certificado Vigilancia',
		gaResponse,
		gaValue: '',
		gaNonInteraction: false,
		id: azureObjectId,
		timeStamp: moment().format(import.meta.env.REACT_APP_GTM_MOMENT_FORMAT),
	};

	TagManager.dataLayer({
		dataLayer,
		dataLayerName: 'PageDataLayer',
	});

	sendTagLog(dataLayer);
};

export const {
	loadingPdfVigilancia,
	successPdfVigilancia,
	errorPdfVigilancia,
	loadingVigilancia,
	successVigilancia,
	errorVigilancia,
	setPageVigilancia,
	setTotalVigilancia,
	successPdfDownloadVigilancia,
	loadingPdfDownloadVigilancia,
} = vigilanciaSlice.actions;

export const useVigilanciaSelector = () =>
	useSelector<AppState, VigilanciaState>(
		({ [vigilanciaSlice.name]: slice }) => slice,
	);

export const fetchPdfVigilanciaThunk =
	(
		Id: string,
		fechaRealizacion: string,
		download?: boolean,
		agent?: string,
		fecha1raVisualizacion?: string | null | undefined,
	) =>
	async (dispatch: Dispatch): Promise<any> => {
		dispatch(loadingPdfVigilancia());
		const axiosApi = AxiosClient(`${import.meta.env.REACT_APP_API_ACHS}`);
		let vigilanciaData: VigilanciaPdfResponse;
		try {
			if (!fecha1raVisualizacion) {
				await axiosApi.put(
					'bff-vigilancia/v1/vigilancia/updateFirstView',
					{
						idExamen: Id,
					},
				);
			}
			const response = await axiosApi.get(
				`bff-vigilancia/v1/vigilancia/certificadoPDF?idExamen=${Id}`,
			);
			vigilanciaData = response.data;

			if (download && agent) {
				const linkSource = `data:application/pdf;base64,${vigilanciaData.certificado}`;
				const downloadLink = document.createElement('a');
				const fileName = `${agent} ${fechaRealizacion
					.split('/')
					.join('-')}`;

				downloadLink.href = linkSource;
				downloadLink.download = fileName;
				downloadLink.click();
				dispatch(successPdfDownloadVigilancia());
				dispatch(successPdfVigilancia(vigilanciaData));
				toast.success('Descarga realizada con éxito', toastConfig);
				callTagManagerVigilancia('Éxito');
			} else {
				dispatch(successPdfVigilancia(vigilanciaData));
			}
			return vigilanciaData;
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				dispatch(errorPdfVigilancia(error.message));
			}
			toast.error(
				'Descarga no realizada. Vuelva a intentarlo',
				toastConfig,
			);
			callTagManagerVigilancia('Error');
		}
	};

export const fetchVigilanciaThunk =
	(page: number) =>
	async (dispatch: Dispatch): Promise<void> => {
		const axiosApi = AxiosClient(`${import.meta.env.REACT_APP_API_ACHS}`);
		dispatch(loadingVigilancia());
		try {
			const response = await axiosApi.get(
				`bff-vigilancia/v1/vigilancia/certificados?pagina=${page}&cantidad=6`,
			);
			const vigilanciaData: VigilanciaCertificadosRespose = response.data;
			dispatch(successVigilancia(vigilanciaData));
			dispatch(setTotalVigilancia(vigilanciaData.total));
		} catch (error: unknown) {
			if (axios.isAxiosError(error)) {
				dispatch(errorVigilancia(error.response?.data.message));
			}
		}
	};

export const setPagesVigilancia =
	(page: number) =>
	async (dispatch: Dispatch): Promise<void> => {
		dispatch(setPageVigilancia(page));
	};

export default vigilanciaSlice.reducer;
